import { memo, useLayoutEffect, useRef, useState, useEffect } from "react";
import classNames from "classnames";
import * as PropTypes from "prop-types";
import { useDrop } from "react-dnd";
import { shallowEqual, useSelector, useDispatch, useStore } from "react-redux";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { sanitizeHtml } from "../../../functions/sanitizeHtml";

import { isCurrentDay, isPast } from "../index";
import { DraggableTypes } from "../../utility/DraggableTypes";
import { AlternateIconEvent } from "../../utility/AlternateIconEvent";
import { openModal } from "../../../store/modal";
import { setGridDragDay } from "../../../store/calendar";
import { isCurrentWeekAndYear, isFirstDayOfTheCurrentWeek, isLastDayOfTheCurrentWeek } from "../index";
import "./DayAsTile.scss";
import { getEventsAPI, moveEventAPI, updateEventAPI } from "../../../api";
import { parseEventDate } from "../../../api/eventAPI";
import { NO_ROOM_MESSAGE, eventsOnDay, hasRoomFor, isDayFullError, rearranged } from "../../../functions/dayLimit";
import { moveEvent, getEventsForDate, setRowForEventPosition, updateEvent, updateEventsPositions } from "../../../store/events";
import { EventItem } from "../Event/EventItem";
import { selectHighlightStartingOn, selectThematicsForDay, setHighlightedThematic } from "../../../store/thematics";
import { hoverDay, leaveDay } from "../shortcuts";
import GridAsTile from "../GridLayout/GridAsTile";

dayjs.extend(isBetween);

DayAsTile.propTypes = {
    period: PropTypes.any,
    day: PropTypes.any,
    events: PropTypes.any,
    isSameDate: PropTypes.func,
    event: PropTypes.func,
    isLoweredOpacity: PropTypes.bool,
};

function DayAsTile(props) {
    const dispatch = useDispatch();
    const store = useStore();
    const ref = useRef();
    const date = dayjs(props.day.date).format('YYYY-MM-DD');
    const eventsForDate = useSelector(state => getEventsForDate(state, date));
    const holidays = ["1-1", "2-1", "8-3", "21-3", "22-3", "23-3", "1-5", "7-5", "9-5", "6-7", "1-12", "16-12"];
    const thematicsForDay = useSelector(state => selectThematicsForDay(state, date), shallowEqual);
    const highlightLabel = useSelector(state => selectHighlightStartingOn(state, date));
    const showPastDays = useSelector(state => state.settings.showPastDays);
    const design = useSelector(state => state.settings.design);
    // Measured instead of read during render, since a memoized tile rarely re-renders on its own.
    const [tileSize, setTileSize] = useState({ height: undefined, width: undefined });
    const tileHeight = tileSize.height;
    const tileWidth = tileSize.width;

    useLayoutEffect(() => {
        const td = ref.current;
        const measure = () => setTileSize(size => (
            size.height === td.offsetHeight && size.width === td.offsetWidth
                ? size
                : { height: td.offsetHeight, width: td.offsetWidth }
        ));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(td);
        return () => observer.disconnect();
    }, []);

    const calculateTitleHeight = () => {
        if (design === 'Original') return tileHeight;
        if (design === 'Skeuomorphism') {
            switch (props.period) {
                case 1:
                    return tileHeight - 2.5;
                case 3:
                    return tileHeight - 2.5;
                case 6:
                    return tileHeight - 2.5;
                case 7:
                    return tileHeight - 5;
                case 12:
                    return tileHeight - 2;


            }
        }
    }
    const [extraEvents, setExtraEvents] = useState(0);
    const alternate = props.alternate;
    const table = props.table;
    let events = eventsForDate;

    const overlapDiv = useRef()
    const [{ isOver, canDrop, refused }, drop] = useDrop({
        accept: DraggableTypes.EVENT,
        collect: (monitor) => {
            const over = monitor.isOver({ shallow: true });
            return {
                isOver: over,
                canDrop: monitor.canDrop(),
                // Held over another day that has no room for it.
                refused: over && !monitor.canDrop() && !dayjs(monitor.getItem()?.date).isSame(date, 'day'),
            };
        },
        drop: (item, monitor) => {
            if (!dayjs(item.date).isSame(date, 'day')) {
                const event = { ...item, date: props.day.date };
                updateEventAPI(event).then((res) => {
                    dispatch(updateEvent(parseEventDate(res.data)));
                    if (rearranged(res).length) dispatch(updateEventsPositions(rearranged(res)));
                }).catch((e) => {
                    if (isDayFullError(e)) alert(NO_ROOM_MESSAGE);
                    else console.log(e);
                })
            }
        },
        canDrop: (item) => !dayjs(item.date).isSame(date, 'day') && hasRoomFor(eventsOnDay(store.getState(), date), item),
    }, [events]);
    if (alternate) {
        events = eventsForDate.slice(0, 8);
    }

    useEffect(() => {
        if (eventsForDate.length == 0) {
            setExtraEvents(0);
        }
    }, [eventsForDate])

    const setSelectedDate = (date) => {
        dispatch(openModal({
            date: date,
            updateID: null,
            isNew: false,
        }));
    };

    // mouseover fires for every child the pointer crosses, so only dispatch actual changes.
    const setThematic = () => {
        const thematic = thematicsForDay.length > 0 ? thematicsForDay[0] : null;
        if (store.getState().thematics.highlightedThematics !== thematic) {
            dispatch(setHighlightedThematic(thematic))
        }
    }

    const onMouseLeave = (e) => {
        leaveDay();

        if (store.getState().thematics.highlightedThematics !== null) {
            dispatch(setHighlightedThematic(null))
        }
        if (events.length === 0 || ref === null || ref.current === null) return;
        const container = ref.current;
        container.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const onMouseEnter = (e) => {
        hoverDay(props.day.date);
        // With Ctrl held, the day under the pointer is the one whose events can be moved.
        const { gridDragMode, gridDragDay } = store.getState().calendar;
        if (gridDragMode && gridDragDay !== date) dispatch(setGridDragDay(date));
    }

    const isToday = isCurrentDay(props.day.date);
    const isPastDay = isPast(props.day.date);
    const weekday = props.day.date.day();

    return <td
        ref={el => { ref.current = el; }}
        onMouseEnter={onMouseEnter}
        onMouseOver={e => { e.preventDefault(); setThematic() }}
        onMouseLeave={e => { e.preventDefault(); onMouseLeave() }}
        className={classNames(
            {
                "day-of-month": props.period < 3,
                "day-of-month--current-day": isToday,
                "day-of-month--past-day": isPastDay && showPastDays,
                "day-of-month--not-this-month": !props.day.isCurrentPeriod,
                "period-is-quarter-year-day": props.period === 3,
                "period-is-third-of-year-day": props.period === 4,
                "period-is-half-year-day": props.period === 6,
                "period-is-week": props.period === 7,
                "period-is-year-day": props.period === 12,
                "calendar-day-container": true,
                'alternate': alternate,
                "alternate-current-day": isToday && alternate,
                "alternate-past-day": isPastDay && alternate && showPastDays,
                "table-day": table,
                "table-current-day": isToday && table,
                "table-past-day": isPastDay && table && showPastDays,
                "monday": weekday === 1,
                "tuesday": weekday === 2,
                "wednesday": weekday === 3,
                "thursday": weekday === 4,
                "friday": weekday === 5,
                "saturday": weekday === 6,
                "sunday": weekday === 0,
                "holiday": holidays.includes(`${props.day.date.date()}-${+props.day.date.month() + 1}`),
                "thematic-cell": thematicsForDay.length,
                'low-opacity': props.isLoweredOpacity,
                // 'current-weeks-day': isCurrentWeekAndYear(props.day.date),
                // 'first-day-of-the-current-week': isFirstDayOfTheCurrentWeek(props.day.date),
                // 'last-day-of-the-current-week': isLastDayOfTheCurrentWeek(props.day.date)
            }
        )}
    >
        {
            highlightLabel != null && (
                <div className="thematic-highlight" >
                    <span style={{ color: 'black', maxWidth: `${tileWidth}px` }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(highlightLabel) }}
                    ></span>

                </div>
            )
        }

        <span className='day-icon calendar-day-container__number' onClick={() => setSelectedDate(props.day.date)}>{props.day.dayOfMonth}</span>
        {
            extraEvents > 0 && <span className="calenday-day-container__extra" dangerouslySetInnerHTML={{ __html: design == 'Skeuomorphism' ? events.length : `+${extraEvents}` }}></span>
        }

        <div
            ref={drop}
            style={{
                width: '100%', height: '100%',
                border: isOver && canDrop ? '2px dashed green' : refused ? '2px dashed #EB5757' : ''

            }}
        >
            {thematicsForDay.map(thematic => <div className="thematic" key={thematic.id} style={{
                backgroundColor: thematic.color
            }}>
            </div>)}

            {eventsForDate.length > 0 &&
                <GridAsTile
                    events={events}
                    date={date}
                    tileHeight={calculateTitleHeight()}
                    setExtraEvents={setExtraEvents}
                    maxRows={props.maxRows}
                />
            }
        </div>
    </td >;
}

// Parents rebuild the day objects on every render; a tile only needs to re-render when its day or layout changes.
const isSameTile = (prev, next) =>
    prev.day.date.valueOf() === next.day.date.valueOf() &&
    prev.day.dayOfMonth === next.day.dayOfMonth &&
    prev.day.isCurrentPeriod === next.day.isCurrentPeriod &&
    prev.period === next.period &&
    prev.alternate === next.alternate &&
    prev.table === next.table &&
    prev.maxRows === next.maxRows &&
    prev.isLoweredOpacity === next.isLoweredOpacity;

const MemoizedDayAsTile = memo(DayAsTile, isSameTile);

export { MemoizedDayAsTile as DayAsTile };